import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Send, X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function StickyCTA() {
  const { lang } = useLang();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  const content = lang === 'ru' ? {
    desktop: 'Связаться',
    call: 'Позвонить',
    telegram: 'Telegram',
  } : {
    desktop: 'Contactar',
    call: 'Llamar',
    telegram: 'Telegram',
  };

  useEffect(() => {
    const handleScroll = () => {
      // Show after 500px scroll
      if (window.scrollY > 500 && !isClosed) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isClosed]);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsClosed(true);
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Desktop: Fixed button bottom-right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="hidden md:block fixed bottom-6 right-6 z-50 group"
          >
            <a
              href="tel:+34624586917"
              className="relative btn-call flex items-center gap-3 px-6 py-4 rounded-full text-base font-bold shadow-2xl hover:shadow-[0_20px_60px_rgba(212,175,55,0.5)] transition-all duration-300"
              style={{
                boxShadow: '0 10px 40px rgba(212,175,55,0.4), 0 0 0 4px rgba(212,175,55,0.2)',
              }}
            >
              <Phone size={20} className="animate-pulse" />
              <span>{content.desktop}</span>
              
              {/* Ripple effect */}
              <span
                className="absolute inset-0 rounded-full opacity-75 animate-ping"
                style={{
                  background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
                  animationDuration: '2s',
                }}
              />
            </a>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#0A0B0F] border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              aria-label="Close"
            >
              <X size={14} className="text-white" />
            </button>
          </motion.div>

          {/* Mobile: Sticky bottom bar with 2 actions */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden fixed bottom-0 left-0 right-0 z-50"
          >
            <div className="relative bg-gradient-to-r from-[#0A0B0F]/95 via-[#1A1B1F]/95 to-[#0A0B0F]/95 backdrop-blur-xl border-t border-[#D4AF37]/20 shadow-2xl">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center"
                aria-label="Close"
              >
                <X size={14} className="text-white" />
              </button>

              <div className="flex items-center gap-3 px-4 py-4">
                <a
                  href="tel:+34624586917"
                  className="btn-call flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold flex-1 shadow-xl"
                >
                  <Phone size={18} />
                  {content.call}
                </a>
                <a
                  href="https://t.me/SerjValencia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-telegram flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold flex-1 shadow-xl"
                >
                  <Send size={18} />
                  {content.telegram}
                </a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
